/**
 * Editing your own message, in its row: the body becomes a field, with Cancel
 * and Save under it.
 *
 * Enter saves and Shift+Enter is a new line, as in the composer; Escape
 * cancels. A body that is empty once trimmed saves nothing — deleting is the
 * menu's other entry — and one that has not changed closes without sending an
 * edit.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { store } from '../../lib/relay/store';
import type { Message } from '../../lib/relay/types';
import './chat.css';

/** What Save does with `body`: nothing, close without sending, or send. */
export function saveAction(original: string, body: string): 'stay' | 'close' | 'send' {
  if (!body.trim()) return 'stay';
  return body === original ? 'close' : 'send';
}

export function MessageEditor({ message, onClose }: { message: Message; onClose: () => void }) {
  const { Button } = ds();
  const t = useT();
  const [body, setBody] = useState(message.body);
  const field = useRef<HTMLTextAreaElement>(null);

  // Focused with the caret at the end, as if you had just typed it.
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // As tall as what it holds, up to the stylesheet's cap. `scrollHeight`
  // leaves out the border, which a border-box height includes.
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [body]);

  const save = () => {
    const action = saveAction(message.body, body);
    if (action === 'stay') return;
    if (action === 'send') store.editMessage(message.conversationId, message.id, body);
    onClose();
  };

  return (
    <div className="rl-edit">
      <textarea
        ref={field}
        className="rl-edit-field"
        value={body}
        rows={1}
        aria-label={t('conversation.edit.title')}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            save();
          }
        }}
      />
      <div className="rl-edit-actions">
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={save} disabled={!body.trim()}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
